import { useEffect } from 'react';
import { addMinutes, fromUnixTime, getUnixTime } from 'date-fns';
import { isNumber } from 'lodash-es';
import { proxy, snapshot, useSnapshot } from 'valtio';
import { formatEther } from 'viem';
import { z } from 'zod';
import type { MintSchedule } from '@soundxyz/sdk';
import type { EnumValueType } from '@soundxyz/utils/const';
import {
  AuctionTypeEnum,
  AuctionTypenames,
  createEnum,
  CreditRoleEnum,
  enumChecker,
  enumParser,
  OPEN_EDITION_MAX_MINTABLE,
  OPEN_EDITION_MAX_MINTS_PER_WALLET_OPTIONS,
  ReleaseTypeEnum,
} from '@soundxyz/utils/const';
import type { Maybe } from '@soundxyz/utils/helpers';
import { getFromList } from '@soundxyz/utils/helpers';
import { useLatestRef } from '@components-shared/src/hooks/useLatestRef';
import { useAuth } from '@context/auth/base';
import { useChainProvider } from '@context/chainProvider';
import type {
  ArtistMintingOptionsQuery,
  AuctionConfigurationInput,
  DraftInfoInput,
  GetDraftDataQuery,
} from '@graphql/reactQuery';
import {
  AllowlistSourceType,
  AuctionType,
  gql,
  LicenseType,
  MediaType,
  ReleaseByIdDocument,
  SongKeyType,
  useQuery,
  useReactQuery,
} from '@graphql/reactQuery';
import { useEditionInfo } from '@store/sdk/reactQuery';
import { isDeployedContractAddress } from '@utils/chain';
import { UPLOAD_STEP_NAMES_OPEN_UP_SOUND, UPLOAD_STEPS_OPEN_UP_SOUND } from '@utils/constants';
import { FormValidationError } from '@utils/error';
import { getSingularOrPlural, stepIdToTypename } from '@utils/helpers';
import { ErrorMessages as ErrorMessagesV2 } from '../upload-new/auction-config/form/errors';
import type { CollectorsType } from './AuctionConfiguration/AllowlistForm/Upload';
import { PRESALE_MINT_COPY, PUBLIC_MINT_COPY } from './AuctionConfiguration/copy';
import type { EditAuctionConfigurationErrorsType } from './AuctionConfiguration/edit/errors';
import type {
  EditedSaleScheduleToUpdate,
  MerkleDropSaleScheduleUpdate,
  RangeEditionSaleScheduleUpdate,
} from './AuctionConfiguration/edit/fields/auctionConfig';
import {
  EditedAuctionConfigPersistence,
  editedAuctionConfigState,
} from './AuctionConfiguration/edit/fields/auctionConfig';
import { mapPersistedAllowlistsToFormValues } from './AuctionConfiguration/edit/helpers';
import { useEditedAllowlistsFromPersistence } from './AuctionConfiguration/edit/useEditedAuctionConfigForm';
import { validateEditedAuctionConfigState } from './AuctionConfiguration/edit/validation';
import { ErrorMessages } from './AuctionConfiguration/errors';
import {
  auctionConfigurationState,
  resetAuctionConfigurationState,
} from './AuctionConfiguration/fields';
import { mapAuctionConfigurationToFormValues } from './AuctionConfiguration/helpers';
import { validateAuctionConfiguration } from './AuctionConfiguration/validation';
import { getEditedReleaseInfoPersistence, releaseInfoState } from './ReleaseInfoForm/Shared/fields';
import {
  mapDeployedReleaseInfoToFormValues,
  mapDraftReleaseInfoToFormValues,
  resetReleaseInfoState,
  setAllReleaseInfoFieldsTouched,
} from './ReleaseInfoForm/Shared/helpers';
import { validateReleaseInfo } from './ReleaseInfoForm/Single/Shared/validation';
import { getEditedSplitsPersistence, useEditedSplitsFromPersistence } from './SplitsForm/edit';
import { initializeSplitsFieldsWithOwnerAddress, resetSplitsState } from './SplitsForm/fields';
import {
  mapDraftSplitsToFormValues,
  mapPersistedSplitsToFormValues,
  mapReleaseSplitsToFormValues,
} from './SplitsForm/helpers';
import { validateSplits } from './SplitsForm/validation';
import { useDraftData } from './useDraftData';

/*#__PURE__*/ gql(/* GraphQL */ `
  query ReleaseById($id: UUID!) {
    release(id: $id) {
      id
      auctionContractType
      behindTheMusic
      canCollectorsDownloadAudio
      chainId
      contract {
        contractAddress
        id
      }
      coverImage {
        id
        key
      }
      creditSplit {
        id
        splitAddress
        creditAllocations {
          id
          owner {
            id
            publicAddress
          }
          percent
          roles
        }
      }
      draftId
      genre {
        id
        name
      }
      goldenEggImage {
        id
        key
      }
      publicAffiliateFeeBPS
      metadataDetails {
        bpm
        key
        license
        location
        lyrics
      }
      rewards {
        description
        id
        title
      }
      staticCoverImage {
        id
        key
      }
      title
      track {
        audio {
          audio192k {
            id
            key
          }
          audioOriginal {
            id
            key
          }
        }
        duration
        id
        normalizedPeaks
        title
      }
    }
  }
`);

export type DraftType = 'upload' | 'edit';

export const ReleaseTypeUpload = createEnum([ReleaseTypeEnum.SINGLE, ReleaseTypeEnum.ALBUM]);
export type ReleaseTypeUpload = EnumValueType<typeof ReleaseTypeUpload>;

export const parseReleaseTypeUpload = enumParser(ReleaseTypeUpload);

export const isReleaseTypeUpload = enumChecker(ReleaseTypeUpload);

export interface UploadToolProps {
  draftId: string;
  stepId: number;
  releaseType: ReleaseTypeUpload;
}

const SavedStepDataReady = proxy({
  stepId: 0,
  isReady: false,
});

export function useIsSavedStepDataReady({ stepId }: { stepId: number }) {
  const storeState = useSnapshot(SavedStepDataReady);

  return storeState.stepId === stepId && storeState.isReady;
}

const getAuctionOptions = ({
  type,
  data,
}: {
  type: Maybe<AuctionType>;
  data: Maybe<ArtistMintingOptionsQuery['artistMintingOptions']>;
}) => {
  switch (type) {
    case 'FIXED_QUANTITY': {
      return {
        type,
        data: getFromList(
          data?.auction,
          options => options.__typename === AuctionTypenames.FixedAuction && options,
        ),
      };
    }
    case 'OPEN_EDITION': {
      return {
        type,
        data: getFromList(
          data?.auction,
          options => options.__typename === AuctionTypenames.OpenEditionAuction && options,
        ),
      };
    }
    case 'OPEN_EDITION_WITH_SAM': {
      return {
        type,
        data: getFromList(
          data?.auction,
          options => options.__typename === AuctionTypenames.OpenEditionWithSAMAuction && options,
        ),
      };
    }
    default:
      return {};
  }
};

/**
 * Checks if SAM is enabled for the given auction options
 */
export const isSamEnabled = ({
  type,
  data,
}: {
  type: Maybe<AuctionType>;
  data: Maybe<ArtistMintingOptionsQuery['artistMintingOptions']>;
}) => {
  const options = getAuctionOptions({ type, data });

  /**
   * SAM is only enabled if inflectionCount and inflectionPrice are set for the specific auction type
   */
  return (
    options.data?.__typename === 'OpenEditionWithSAMAuction' &&
    options.data.inflectionCountSAM &&
    options.data.inflectionMultipleBPS
  );
};

export const getURIFromArweaveHash = ({ arweaveHash }: { arweaveHash: string }) => {
  return `ar://${arweaveHash}`;
};

export const getAuctionType = (
  typename: Maybe<ArtistMintingOptionsQuery['artistMintingOptions']['auction'][0]['__typename']>,
): AuctionTypeEnum | null => {
  switch (typename) {
    case 'FixedAuction':
      return 'FIXED_QUANTITY';
    case 'OpenEditionAuction':
      return 'OPEN_EDITION';
    case 'OpenEditionWithSAMAuction':
      return 'OPEN_EDITION_WITH_SAM';
    default:
      return null;
  }
};

export const useSetFormDataFromQuery = ({
  draftId,
  stepId,
  draftType,
  releaseType,
}: {
  draftId: string | undefined;
  stepId: number | undefined;
  draftType: DraftType;
  releaseType?: ReleaseTypeEnum;
}) => {
  const { draft: data, loading } = useDraftData({
    draftId,
    disabled: draftType !== 'upload',
  });

  const { data: deployedReleaseData } = useQuery(
    ReleaseByIdDocument, // Replace for custom query to avoid over/underfetching
    {
      variables: !!data?.release?.id && { id: data.release.id },
      enabled: draftType === 'edit',
      staleTime: 0,
      cacheTime: Infinity,
    },
  );

  const deployedEditionInfo = useEditionInfo(
    {
      auctionContractType: deployedReleaseData?.data.release?.auctionContractType || null,
      chainId: deployedReleaseData?.data.release?.chainId || 1,
      editionAddress: deployedReleaseData?.data.release?.contract.contractAddress || '',
    },
    {
      enabled:
        !!deployedReleaseData?.data.release?.contract.contractAddress &&
        !!deployedReleaseData?.data.release?.chainId,
    },
  );

  const { authUser } = useAuth();
  const { persistedSplits } = useEditedSplitsFromPersistence({ releaseId: data?.release?.id });
  const { persistedAllowlists } = useEditedAllowlistsFromPersistence({
    releaseId: data?.release?.id,
  });
  const { isReady } = useSnapshot(SavedStepDataReady);

  const deployedReleaseChain = deployedReleaseData?.data.release?.chainId;
  const deployedReleaseSplitsContractAddress =
    deployedReleaseData?.data.release?.creditSplit?.splitAddress;

  const { provider } = useChainProvider();

  const {
    data: isSplitsContractDeployed = null,
    isInitialLoading: isSplitsContractChainCheckLoading,
  } = useReactQuery({
    queryKey: [
      'splits-contract-matches-chain',
      deployedReleaseChain,
      deployedReleaseSplitsContractAddress,
    ],
    enabled: deployedReleaseChain != null && deployedReleaseSplitsContractAddress != null,
    async queryFn() {
      if (deployedReleaseChain == null || deployedReleaseSplitsContractAddress == null) return null;

      return isDeployedContractAddress({
        contractAddress: deployedReleaseSplitsContractAddress,
        provider: provider(deployedReleaseChain),
      });
    },
    retry: true,
  });

  useEffect(() => {
    SavedStepDataReady.isReady = false;

    if (!releaseType || !stepId || !authUser?.publicAddress) return;

    if (draftType === 'edit') {
      if (isSplitsContractChainCheckLoading || deployedReleaseData == null) return;

      if (deployedReleaseData?.data?.release && deployedEditionInfo.data?.symbol) {
        mapDeployedReleaseInfoToFormValues({
          releaseData: deployedReleaseData?.data.release,
          tokenSymbol: deployedEditionInfo.data.symbol,
        });

        setTimeout(() => {
          SavedStepDataReady.isReady = true;
        });
      }

      const creditAllocations = deployedReleaseData?.data?.release?.creditSplit?.creditAllocations;
      if (persistedSplits) {
        mapPersistedSplitsToFormValues(persistedSplits);
      } else if (creditAllocations && isSplitsContractDeployed) {
        mapReleaseSplitsToFormValues(creditAllocations, authUser.publicAddress);
      } else {
        resetSplitsState();
        initializeSplitsFieldsWithOwnerAddress(authUser.publicAddress);
      }

      if (persistedAllowlists && data?.release?.id) {
        mapPersistedAllowlistsToFormValues({ releaseId: data?.release?.id, persistedAllowlists });
      }

      return;
    }

    SavedStepDataReady.stepId = stepId;

    const draft = data?.info;

    const draftTypename = stepIdToTypename(stepId);

    if (draftTypename === UPLOAD_STEP_NAMES_OPEN_UP_SOUND.RELEASE_INFO) {
      // TODO: Update this when we remove react-hook-form from album release info form
      // https://linear.app/sound/issue/ENG-6100/remove-react-hook-form-from-release-info-album
      if (draft?.releaseInfo && releaseType) {
        mapDraftReleaseInfoToFormValues({
          releaseType,
          data: { ...draft.releaseInfo, ...draft.rewardsInfo },
        });
      } else {
        resetReleaseInfoState();
      }
    }

    if (draftTypename === UPLOAD_STEP_NAMES_OPEN_UP_SOUND.SPLITS) {
      if (draft?.splitsInfo) {
        mapDraftSplitsToFormValues(draft.splitsInfo);
      } else {
        resetSplitsState();
        initializeSplitsFieldsWithOwnerAddress(authUser.publicAddress);
      }
    }

    if (draftTypename === UPLOAD_STEP_NAMES_OPEN_UP_SOUND.DROP_CONFIGURATION) {
      if (draft?.auctionConfigurations) {
        mapAuctionConfigurationToFormValues(
          draft.auctionConfigurations,
          draft.allowListInfo,
          draftType,
        );
      } else {
        resetAuctionConfigurationState();
      }
    }

    setTimeout(() => {
      SavedStepDataReady.isReady = true;
    });
  }, [
    data,
    stepId,
    draftType,
    releaseType,
    authUser?.publicAddress,
    deployedReleaseData,
    deployedEditionInfo.data?.symbol,
    persistedSplits,
    persistedAllowlists,
    isSplitsContractChainCheckLoading,
    isSplitsContractDeployed,
  ]);

  return {
    loading,
    // Make sure to pass snapshot so we trigger a re-render when the value changes
    isReady,
  };
};

const getUploadStepNameNew = (releaseType: ReleaseTypeUpload, stepId: number) => {
  // If release type is album and step is 1, return album info
  if (releaseType === ReleaseTypeEnum.ALBUM && stepId === UPLOAD_STEPS_OPEN_UP_SOUND[0]?.step) {
    return UPLOAD_STEP_NAMES_OPEN_UP_SOUND.ALBUM_INFO;
  }

  // Else return the shared name of both release types
  return UPLOAD_STEPS_OPEN_UP_SOUND[stepId - 1]?.name ?? '';
};

/**
 * @deprecated Only used in Albums, should delete soon https://linear.app/sound/issue/ENG-6332/update-form-in-album-upload-flow
 * */
export function useCheckSelectOptionValue<Value extends string | number>({
  value,
  options,
  resetValue,
}: {
  value: Maybe<Value>;
  options: number[];
  resetValue: () => void;
}) {
  const latestResetValue = useLatestRef(resetValue);
  useEffect(() => {
    if (value != null && !options.includes(Number(value))) {
      latestResetValue.current();
    }
  }, [value, options, latestResetValue]);
}

export const numericInteger = z.union([z.string(), z.number()]).transform(v => {
  const stringNum = v.toString();
  return stringNum ? parseInt(stringNum, 10) : null;
});

export const numericFloat = z.union([z.string(), z.number()]).transform(v => {
  const stringNum = v.toString();
  return stringNum ? parseFloat(stringNum) : null;
});

const allowList = z
  .array(
    z.object({
      address: z.string(),
      source: z.nativeEnum(AllowlistSourceType),
    }),
  )
  .nullish()
  .transform(v => v ?? []);

export const ReleaseTypeStepSchema = z.object({
  releaseType: z.nativeEnum(ReleaseTypeEnum),
});

export const PriceStepSchema = z.object({
  presalePricePerMint: numericFloat.nullish(),

  publicPricePerMint: numericFloat.nullish(),
});

const DatesStepSchema = z.object({
  presaleStartTime: z.date().nullish(),
  presaleEndTime: numericInteger.nullish(),
  publicStartTime: z.date().nullish(),
  publicEndTime: numericInteger.nullish(),
});

export const AuctionConfigStepSchema = z
  .object({
    auctionType: z.nativeEnum(AuctionTypeEnum).nullish(),
    maxQuantity: numericInteger.nullish(),
    minQuantity: numericInteger.nullish(),
    maxMintable: numericInteger.nullish(),

    presaleAllowList: allowList,
    presaleMaxSupply: numericInteger.nullish(),
    presaleMaxMintsPerWallet: numericInteger.nullish(),
    publicMaxMintsPerWallet: numericInteger.nullish(),

    quantityBreakdown: z
      .array(numericInteger.transform(v => v ?? 0))
      .nullish()
      .default(null),

    /**
     * Toggle to enable SAM for auction.
     */
    enabledSAM: z.boolean(),
  })
  .merge(PriceStepSchema)
  .merge(DatesStepSchema);

export const UploadedMediaSchema = z.object({
  mediaType: z.nativeEnum(MediaType),
  uploadKey: z.string(),
});

export type UploadedMediaType = (typeof UploadedMediaSchema)['_input'];

export const TrackUploadSchema = z.object({
  coverImage: UploadedMediaSchema.nullish().default(null),
  duration: z.number().optional().default(0),
  fileDetail: UploadedMediaSchema,
  normalizedPeaks: z.array(z.number()).optional().default([]),
  title: z.string(),
});

export const TempTrackStepSchema = z.object({
  tempTitle: z.string().nullish(),
  tempAudio: TrackUploadSchema.nullable(),
  tempCoverImage: UploadedMediaSchema.nullish(),
});

export const ReleaseInfoStepSchema = z.object({
  behindTheMusic: z.string().default(''),
  coverImage: UploadedMediaSchema,
  staticCoverImage: UploadedMediaSchema.nullish().default(null),
  genre: z.string(),
  title: z.string(),
  tokenSymbol: z.string(),
  tracks: z.array(TrackUploadSchema.nullable()),
  goldenEggImages: z.array(UploadedMediaSchema),
  canCollectorsDownloadAudio: z.boolean().default(true),
  curatorRewardsEnabled: z.boolean().default(true),
  rewards: z
    .array(
      z.object({
        description: z.string(),
        title: z.string(),
      }),
    )
    .nullish()
    .default(null),
});

export const MetadataStepSchema = z.object({
  beatsPerMinute: numericInteger.nullish().default(null),
  key: z.nativeEnum(SongKeyType).nullish().default(null),
  lyrics: z.string().nullish().default(null),
  license: z.nativeEnum(LicenseType).nullish().default(null),
  location: z.string().nullish().default(null),
});

export const SplitsStepSchema = z.object({
  splits: z
    .array(
      z.object({
        ownerAddress: z.string(),
        percent: numericFloat.transform(v => v ?? 0),
        roles: z.array(z.nativeEnum(CreditRoleEnum)),
      }),
    )
    .nullish()
    .transform(v => v ?? []),
});

export const CollectorsOfReleasesSchema = z
  .object({
    allArtistReleases: z.boolean(),
    allArtistCollaborations: z.boolean(),
    selectedReleaseIds: z.array(z.string()).nullable(),
  })
  .nullish()
  .transform(o => {
    return {
      allArtistReleases: o?.allArtistReleases ?? false,
      allArtistCollaborations: o?.allArtistCollaborations ?? false,
      selectedReleaseIds: o?.selectedReleaseIds ?? [],
    };
  });

export const CollectorsOfArtistsSchema = z
  .object({
    allArtistCollectors: z.boolean(),
    allCollaboratingArtists: z.boolean(),
    selectedArtistIds: z.array(z.string()).nullable(),
  })
  .nullish()
  .transform(o => {
    return {
      allArtistCollectors: o?.allArtistCollectors ?? true,
      allCollaboratingArtists: o?.allCollaboratingArtists ?? false,
      selectedArtistIds: o?.selectedArtistIds ?? [],
    };
  });

const AllowlistSchema = z
  .object({
    collectorsOfReleases: CollectorsOfReleasesSchema,
    collectorsOfArtists: CollectorsOfArtistsSchema,
    manuallyAddedAllowlist: z
      .object({
        description: z.string().nullish(),
        hasAddresses: z.boolean().nullish(),
      })
      .nullish(),
  })
  .nullish();

export const AllowlistStepSchema = z.object({
  presaleMint: AllowlistSchema,
});

export const DeployInputSchema = z.object({
  curatorRewards: z.boolean().default(true),
  chainId: z.number().default(1),
});

export const FormDataUnionSchema = z.union([
  ReleaseTypeStepSchema,
  ReleaseInfoStepSchema,
  TempTrackStepSchema,
  MetadataStepSchema,
  SplitsStepSchema,
  AuctionConfigStepSchema,
  AllowlistStepSchema,
  DeployInputSchema,
]);

export type FormDataUnion = (typeof FormDataUnionSchema)['_input'];

export const ProcessLoadingState = proxy({
  loadingCount: 0,
});

export const SecondaryProcessLoadingState = proxy({
  loadingCount: 0,
});

export const useIsProcessLoading = () => useSnapshot(ProcessLoadingState).loadingCount > 0;
export const useIsSecondaryProcessLoading = () =>
  useSnapshot(SecondaryProcessLoadingState).loadingCount > 0;

export const getInfoToUpsertFromFormOrThrow = ({
  releaseType,
  stepId,
  saleData,
  presaleMintTotalUsers,
}: {
  releaseType: ReleaseTypeUpload;
  stepId: number;
  saleData: GetDraftDataQuery | undefined;
  presaleMintTotalUsers: number | undefined;
}): DraftInfoInput => {
  const stepName = getUploadStepNameNew(releaseType, stepId);

  // Set form data to the field corresponding to the current step

  switch (stepName) {
    case UPLOAD_STEP_NAMES_OPEN_UP_SOUND.RELEASE_INFO:
      setAllReleaseInfoFieldsTouched();
      const validReleaseInfoFields = validateReleaseInfo();

      if (!validReleaseInfoFields) {
        throw new FormValidationError('Input error for at least 1 field.');
      }

      const parsedGoldenEggImages = z
        .array(UploadedMediaSchema)
        .safeParse(validReleaseInfoFields.goldenEggImages);
      if (parsedGoldenEggImages.success === false) {
        throw Error('goldenEgg was not set');
      }

      return {
        releaseInfo: {
          title: validReleaseInfoFields.title,
          tokenSymbol: validReleaseInfoFields.tokenSymbol,
          coverImage: validReleaseInfoFields.coverImage,
          tracks: validReleaseInfoFields.tracks || [],
          genre: validReleaseInfoFields.genre,
          behindTheMusic: validReleaseInfoFields.behindTheMusic,
          beatsPerMinute: validReleaseInfoFields.beatsPerMinute ?? null,
          staticCoverImage: validReleaseInfoFields.staticCoverImage,
          key: validReleaseInfoFields.key,
          lyrics: validReleaseInfoFields.lyrics,
          license: validReleaseInfoFields.license,
          location: validReleaseInfoFields.location,
          type: releaseType,
        },
        rewardsInfo: {
          goldenEggImages: parsedGoldenEggImages.data,
          canCollectorsDownloadAudio: validReleaseInfoFields.canCollectorsDownloadAudio,
          curatorRewardsEnabled: validReleaseInfoFields.curatorRewardsEnabled,
          rewards: validReleaseInfoFields.rewards,
        },
      };
    case UPLOAD_STEP_NAMES_OPEN_UP_SOUND.ALBUM_INFO:
      // Set tempTrack to false so we can bypass temptrack modal validation
      // These values are null at this point, so validation won't pass if we don't set this to false
      releaseInfoState.isTempTrack = false;

      const validAlbumReleaseInfoFields = validateReleaseInfo();

      if (!validAlbumReleaseInfoFields) {
        throw new FormValidationError('Input error for at least 1 field.');
      }

      const parsedAlbumGoldenEggImages = z
        .array(UploadedMediaSchema)
        .safeParse(validAlbumReleaseInfoFields.goldenEggImages);
      if (parsedAlbumGoldenEggImages.success === false) {
        throw Error('Not all golden eggs were set');
      }

      return {
        releaseInfo: {
          title: validAlbumReleaseInfoFields.title,
          tokenSymbol: validAlbumReleaseInfoFields.tokenSymbol,
          coverImage: validAlbumReleaseInfoFields.coverImage,
          tracks: validAlbumReleaseInfoFields.tracks || [],
          genre: validAlbumReleaseInfoFields.genre,
          behindTheMusic: validAlbumReleaseInfoFields.behindTheMusic,
          beatsPerMinute: validAlbumReleaseInfoFields.beatsPerMinute ?? null,
          staticCoverImage: validAlbumReleaseInfoFields.staticCoverImage,
          key: validAlbumReleaseInfoFields.key,
          lyrics: validAlbumReleaseInfoFields.lyrics,
          license: validAlbumReleaseInfoFields.license,
          location: validAlbumReleaseInfoFields.location,
          type: releaseType,
        },
        rewardsInfo: {
          goldenEggImages: parsedAlbumGoldenEggImages.data,
          canCollectorsDownloadAudio: validAlbumReleaseInfoFields.canCollectorsDownloadAudio,
          curatorRewardsEnabled: validAlbumReleaseInfoFields.curatorRewardsEnabled,
          rewards: validAlbumReleaseInfoFields.rewards,
        },
      };
    case UPLOAD_STEP_NAMES_OPEN_UP_SOUND.SPLITS:
      const validSplitsFields = validateSplits();
      if (!validSplitsFields) {
        throw new FormValidationError('Input error for at least 1 field.');
      }
      return {
        splitsInfo: { splits: validSplitsFields.splits },
      };

    case UPLOAD_STEP_NAMES_OPEN_UP_SOUND.DROP_CONFIGURATION:
      // Check form validation
      const { hasPresaleMint, fields: auctionConfigurationFields } =
        snapshot(auctionConfigurationState);

      const validAuctionConfigurationFields = validateAuctionConfiguration();

      if (!validAuctionConfigurationFields) {
        throw new FormValidationError('Input error for at least 1 field.');
      }

      /**
       * Open edition -> quantity is uint32 max
       * Range bound -> quantity is upper bound
       * Fixed quantity -> quantity is amount selected in total fixed dropdown
       */
      const totalQuantity =
        auctionConfigurationFields.auctionType === AuctionTypeEnum.OPEN_EDITION ||
        auctionConfigurationFields.auctionType === AuctionTypeEnum.OPEN_EDITION_WITH_SAM
          ? OPEN_EDITION_MAX_MINTABLE
          : auctionConfigurationFields.auctionType === AuctionTypeEnum.RANGE_BOUND
          ? validAuctionConfigurationFields.maxQuantity
          : validAuctionConfigurationFields.maxMintable;

      /**
       * Open edition -> min quantity set from backend
       * Range bound -> min quantity is lower bound
       * Fixed quantity -> min quantity is max mintable
       */
      const minQuantity =
        auctionConfigurationFields.auctionType === AuctionTypeEnum.RANGE_BOUND ||
        auctionConfigurationFields.auctionType === AuctionTypeEnum.OPEN_EDITION ||
        auctionConfigurationFields.auctionType === AuctionTypeEnum.OPEN_EDITION_WITH_SAM
          ? validAuctionConfigurationFields.minQuantity
          : validAuctionConfigurationFields.maxMintable;

      const presaleMint: AuctionConfigurationInput | null = hasPresaleMint
        ? validAuctionConfigurationFields.presalePricePerMint != null &&
          validAuctionConfigurationFields.presaleMaxSupply &&
          validAuctionConfigurationFields.presaleStartTime &&
          validAuctionConfigurationFields.presaleEndTime
          ? {
              price: validAuctionConfigurationFields.presalePricePerMint,
              quantity:
                typeof validAuctionConfigurationFields.presaleMaxSupply !== 'number'
                  ? parseInt(validAuctionConfigurationFields.presaleMaxSupply)
                  : validAuctionConfigurationFields.presaleMaxSupply,
              startTime: getUnixTime(validAuctionConfigurationFields.presaleStartTime),
              endTime: getUnixTime(validAuctionConfigurationFields.presaleEndTime),
              maxMintsPerWallet: (() => {
                if (!validAuctionConfigurationFields.presaleMaxMintsPerWallet) {
                  const error = 'Max mints per wallet is required';
                  auctionConfigurationState.errors.presaleMaxMintsPerWallet = error;
                  throw Error(error);
                }

                return Number(validAuctionConfigurationFields.presaleMaxMintsPerWallet);
              })(),
            }
          : null
        : null;

      const publicMint: AuctionConfigurationInput = {
        price: validAuctionConfigurationFields.publicPricePerMint,
        quantity: Number(totalQuantity),
        startTime: getUnixTime(validAuctionConfigurationFields.publicStartTime),
        endTime: getUnixTime(validAuctionConfigurationFields.publicEndTime),
        maxMintsPerWallet: Number(validAuctionConfigurationFields.publicMaxMintsPerWallet),
      };

      const allowlistInfo =
        validAuctionConfigurationFields && hasPresaleMint
          ? {
              presaleMint: {
                collectorsOfReleases: {
                  allArtistReleases:
                    validAuctionConfigurationFields.collectorsOfReleases?.allArtistReleases ??
                    false,
                  allArtistCollaborations:
                    validAuctionConfigurationFields.collectorsOfReleases?.allArtistCollaborations ??
                    false,
                  selectedReleaseIds:
                    validAuctionConfigurationFields.collectorsOfReleases?.selectedReleaseIds ?? [],
                },
                collectorsOfArtists: {
                  allArtistCollectors:
                    validAuctionConfigurationFields?.collectorsOfArtists?.allArtistCollectors ??
                    true,
                  allCollaboratingArtists:
                    validAuctionConfigurationFields.collectorsOfArtists?.allCollaboratingArtists ??
                    false,
                  selectedArtistIds:
                    validAuctionConfigurationFields.collectorsOfArtists?.selectedArtistIds ?? [],
                },
                manuallyAddedAllowlist: {
                  description: validAuctionConfigurationFields.manuallyAddedAllowlist?.description,
                  hasAddresses:
                    validAuctionConfigurationFields.manuallyAddedAllowlist?.hasAddresses,
                },
              },
            }
          : null;

      const presaleEnabled = !!saleData?.draft.info?.auctionConfigurations?.presaleMint;

      if (
        presaleEnabled &&
        allowlistInfo &&
        !validateSaleHasAllowlist({
          totalUsers: presaleMintTotalUsers,
          sale: allowlistInfo.presaleMint,
        })
      ) {
        throw new FormValidationError(
          'Presale phase must have at least one address in the allowlist',
        );
      }

      // Need to delete before sending to backend otherwise graphql validation fails
      // TODO https://linear.app/sound/issue/ENG-3377/use-backend-draft-zod-schemas-in-frontend
      delete allowlistInfo?.presaleMint?.manuallyAddedAllowlist?.hasAddresses;

      return {
        auctionConfigurations: {
          auctionType: auctionConfigurationFields.auctionType,
          maxMintable: Number(totalQuantity),
          minQuantity: Number(minQuantity),
          presaleMint,
          publicMint,
          quantityBreakdown: validAuctionConfigurationFields.quantityBreakdown,
          enabledSAM: auctionConfigurationFields.enabledSAM,
        },
        allowListsInfo: allowlistInfo,
      };
    default:
      throw new Error(`Invalid upload step name`);
  }
};

export const getFormattedTimezone = () => {
  const now = new Date();
  const formatter = new Intl.DateTimeFormat('en-US', {
    timeZoneName: 'long',
  });

  const dateParts = formatter.formatToParts(now);
  const timezone = dateParts.find(part => part.type === 'timeZoneName');
  return timezone?.value ?? '';
};

const validateSaleHasAllowlist = ({
  sale,
  totalUsers,
}: {
  sale?: z.infer<typeof AllowlistSchema>;
  totalUsers: number | undefined;
}) => {
  return (
    (!!totalUsers && totalUsers > 0) ||
    sale?.collectorsOfArtists.allCollaboratingArtists ||
    sale?.collectorsOfArtists.selectedArtistIds.length ||
    sale?.collectorsOfArtists.allArtistCollectors ||
    sale?.collectorsOfReleases.allArtistCollaborations ||
    sale?.collectorsOfReleases.selectedReleaseIds.length
  );
};

export const FREE_SALE_SCHEDULE_MAX_MINTS_PER_WALLET = 1;

const getTimingErrorMessage = ({ nextMint }: { nextMint: 'public' | 'presale' }) => {
  return `Must be before ${nextMint} mint`;
};

export const getDateTimeErrors = ({
  startTime,
  endTime,
  type,
}: {
  startTime: Date | number | undefined | null;
  endTime: Date | number | undefined | null;
  type: 'presale' | 'public';
}) => {
  const isPresale = type === 'presale';

  const errorMessages: {
    presale?: {
      error: string | undefined;
      path: string;
    };
    public?: {
      error: string | undefined;
      path: string;
    };
  } = {};

  // if no start time return error
  if (startTime === null || startTime === undefined) {
    if (isPresale) {
      errorMessages.presale = {
        error: ErrorMessages.presaleStartTime.required_error,
        path: 'presaleStartTime',
      };
    } else {
      errorMessages.public = {
        error: ErrorMessages.publicStartTime.required_error,
        path: 'publicStartTime',
      };
    }
  }

  // if no end time return error
  if (endTime === null || endTime === undefined) {
    if (isPresale) {
      errorMessages.presale = {
        error: ErrorMessages.presaleEndTime.required_error,
        path: 'presaleEndTime',
      };
    } else {
      errorMessages.public = {
        error: ErrorMessages.publicEndTime.required_error,
        path: 'publicEndTime',
      };
    }
  }

  // if end time < start time return error
  if (startTime != null && endTime != null && endTime < startTime) {
    if (isPresale) {
      errorMessages.presale = {
        error: 'End time must be after start time',
        path: 'presaleEndTime',
      };
    } else {
      errorMessages.public = {
        error: 'End time must be after start time',
        path: 'publicEndTime',
      };
    }
  }

  return errorMessages;
};

export const getDateTimeErrorsV2 = ({
  startTime,
  endTime,
  type,
  auctionType,
}: {
  startTime: Date | number | undefined | null;
  endTime: Date | number | undefined | null;
  type: 'presale' | 'public';
  auctionType: AuctionTypeEnum;
}) => {
  const isPresale = type === 'presale';

  const endTimePath =
    auctionType === AuctionTypeEnum.OPEN_EDITION ? 'publicCutoffTime' : 'publicEndTime';

  const errorMessages: {
    presale?: {
      error: string | undefined;
      path: string;
    };
    public?: {
      error: string | undefined;
      path: string;
    };
  } = {};

  // if no start time return error
  if (startTime === null || startTime === undefined) {
    if (isPresale) {
      errorMessages.presale = {
        error: ErrorMessagesV2.presaleStartTime.required_error,
        path: 'presaleStartTime',
      };
    } else {
      errorMessages.public = {
        error: ErrorMessagesV2.publicStartTime.required_error,
        path: 'publicStartTime',
      };
    }
  }

  // if no end time return error
  if (endTime === null || endTime === undefined) {
    if (isPresale) {
      errorMessages.presale = {
        error: ErrorMessagesV2.presaleEndTime.required_error,
        path: 'presaleEndTime',
      };
    } else {
      errorMessages.public = {
        error: ErrorMessagesV2[endTimePath].required_error,
        path: endTimePath,
      };
    }
  }

  // if end time < start time return error
  if (startTime != null && endTime != null && endTime < startTime) {
    if (isPresale) {
      errorMessages.presale = {
        error: 'End time must be after start time',
        path: 'presaleEndTime',
      };
    } else {
      errorMessages.public = {
        error: 'End time must be after start time',
        path: endTimePath,
      };
    }
  }

  return errorMessages;
};

export const getTimingErrors = ({
  presaleStartTime,
  publicStartTime,
}: {
  presaleStartTime?: Maybe<Date>;
  publicStartTime?: Maybe<Date>;
}) => {
  const errorMessages: { presale?: string } = {};

  presaleStartTime?.setMilliseconds(0);
  publicStartTime?.setMilliseconds(0);

  if (publicStartTime != null && presaleStartTime && presaleStartTime > publicStartTime) {
    errorMessages.presale = getTimingErrorMessage({
      nextMint: 'public',
    });
  }

  return errorMessages;
};

export const getMintType = ({ mintType }: { mintType: 'MerkleDrop' | 'RangeEdition' }) => {
  return mintType === 'MerkleDrop' ? 'Presale' : 'Public Sale';
};

export const getMintCopy = ({
  mintType,
  maxMints,
}: {
  mintType: 'MerkleDrop' | 'RangeEdition';
  maxMints: number | ((unixTimestamp?: number | undefined) => number);
}) => {
  return mintType === 'MerkleDrop'
    ? PRESALE_MINT_COPY(Number(maxMints ?? 0))
    : PUBLIC_MINT_COPY(Number(maxMints ?? 0));
};

export const isPriceZero = (price: string | number | null | undefined) => {
  if (
    (typeof price === 'number' && price === 0) ||
    (typeof price === 'string' && Number(price) === 0)
  ) {
    return true;
  }

  return false;
};

export const getEditDropFormattedData = ({
  mintSchedules,
  persistedSaleSchedules,
  contractAddress,
}: {
  mintSchedules: MintSchedule[];
  persistedSaleSchedules?: EditedSaleScheduleToUpdate[];
  contractAddress: string;
}): EditedSaleScheduleToUpdate[] => {
  return mintSchedules.map((schedule, index) => {
    const persistedSaleSchedule = persistedSaleSchedules
      ? persistedSaleSchedules[index]
      : undefined;

    const price = persistedSaleSchedule ? persistedSaleSchedule.price : formatEther(schedule.price);
    const startTimeSeconds = persistedSaleSchedule
      ? persistedSaleSchedule.startTimeSeconds
      : schedule.startTime;
    const maxMintable = persistedSaleSchedule
      ? persistedSaleSchedule.maxMintable
      : schedule.mintType === 'MerkleDrop'
      ? schedule.maxMintable
      : schedule.maxMintableUpper;
    const maxMintablePerAccount = persistedSaleSchedule
      ? persistedSaleSchedule.maxMintablePerAccount
      : schedule.maxMintablePerAccount;

    const sharedData = {
      editionAddress: contractAddress,
      mintId: schedule.mintId,
      minterAddress: schedule.minterAddress,
      price,
      startTimeSeconds,
      maxMintable,
      maxMintablePerAccount,
    };

    if (schedule.mintType === 'RangeEdition') {
      return {
        ...sharedData,
        cutoffTimeSeconds:
          persistedSaleSchedule && persistedSaleSchedule.mintType === 'RangeEdition'
            ? persistedSaleSchedule.cutoffTimeSeconds
            : schedule.cutoffTime,
        mintType: 'RangeEdition',
      };
    } else {
      return {
        ...sharedData,
        mintType: 'MerkleDrop',
        endTimeSeconds:
          persistedSaleSchedule && persistedSaleSchedule.mintType === 'MerkleDrop'
            ? persistedSaleSchedule.endTimeSeconds
            : schedule.endTime,
      };
    }
  });
};

/**
 * @deprecated Only used in Albums, should delete soon https://linear.app/sound/issue/ENG-6332/update-form-in-album-upload-flow
 * */
export function mintSchedulesWithPriceLimits<
  T extends {
    mintType: MintSchedule['mintType'];
  },
>({
  schedules,
  presaleMinPrice,
  presaleMaxPrice,
  publicMaxPrice,
  publicMinPrice,
  errors,
}: {
  schedules: T[] | undefined;
  presaleMinPrice: number;
  presaleMaxPrice: number;
  publicMinPrice: number;
  publicMaxPrice: number;
  errors: EditAuctionConfigurationErrorsType;
}) {
  return schedules?.map(schedule => ({
    ...schedule,
    minPrice: schedule.mintType === 'MerkleDrop' ? presaleMinPrice : publicMinPrice,
    maxPrice: schedule.mintType === 'MerkleDrop' ? presaleMaxPrice : publicMaxPrice,
    errors: {
      price:
        schedule.mintType === 'MerkleDrop' ? errors.presalePricePerMint : errors.publicPricePerMint,
      startTime:
        schedule.mintType === 'MerkleDrop' ? errors.presaleStartTime : errors.publicStartTime,
    },
  }));
}

export const isPastDate = (date: number | Date) => {
  const formattedDate = typeof date === 'number' ? fromUnixTime(date).getTime() : date.getTime();
  return formattedDate <= Date.now();
};

export function getMaxMintablePerAccountForSaleSchedule({
  auctionType,
  updatedBasePrice,
  updatedMaxMintablePerAccount,
}: {
  auctionType: AuctionType;
  updatedBasePrice: bigint;
  updatedMaxMintablePerAccount: number;
}): number {
  if (updatedBasePrice === 0n) {
    return FREE_SALE_SCHEDULE_MAX_MINTS_PER_WALLET;
  }
  if (auctionType === AuctionType.OpenEdition || auctionType === AuctionType.OpenEditionWithSam) {
    return OPEN_EDITION_MAX_MINTS_PER_WALLET_OPTIONS[0];
  } else {
    return updatedMaxMintablePerAccount;
  }
}

export function roundToNearest30Minutes(date: Date) {
  const minutes = date.getMinutes();
  if (minutes < 30) {
    return addMinutes(date, 30 - minutes);
  } else if (minutes > 30) {
    return addMinutes(date, 60 - minutes);
  }

  return date;
}

export function addZeroBeforeNumber(n: string) {
  const value = parseInt(n, 10);
  if (value < 10 && n.length === 1) {
    return '0' + value;
  }
  return n;
}

export function isDefined<T>(value: T): value is NonNullable<T> {
  return value != null;
}

export function isParseableNumeric<T>(value: T | string | number): value is string | number {
  return (typeof value === 'string' && value !== '') || typeof value === 'number';
}

export function parseDaysToWeeks(value: number): string {
  const quotient = Math.floor(value / 7);
  const remainder = value % 7;
  const isPerfect = remainder === 0;

  if (isPerfect) {
    return `in ${quotient} ${getSingularOrPlural({ count: quotient, word: 'week' })}`;
  }

  return `${value} ${getSingularOrPlural({ count: value, word: 'day' })}`;
}

export const scrollToError = () => {
  // Select the first error element
  const errorElement = document.querySelector('.error');

  if (errorElement) {
    errorElement.scrollIntoView({ behavior: 'smooth', block: 'center' });
  }
};

// Set seconds and milliseconds to zero and returns a new Date or pre-existing one to make comparison easier
export const newCleanDate = (date?: Date) => {
  const newDate = date ?? new Date();
  newDate.setSeconds(0);
  newDate.setMilliseconds(0);

  return newDate;
};

export const getAllowlistPlaceholderText = (entity: CollectorsType) =>
  `Collectors of selected ${entity.toLowerCase()}`;

export const getAllowlistSearchPlaceholderText = (entity: CollectorsType) =>
  `Search ${entity.toLowerCase()} on Sound`;

export const clearAllLocalData = async ({
  contractAddress,
  releaseId,
}: {
  contractAddress: string;
  releaseId: string;
}) => {
  resetReleaseInfoState();
  resetSplitsState();
  editedAuctionConfigState.fields = {
    editedSaleSchedules: {
      [contractAddress]: [],
    },
    allowlists: {
      [releaseId]: [],
    },
  };
  await Promise.all([
    getEditedReleaseInfoPersistence({ releaseId }).clear(),
    getEditedSplitsPersistence({ releaseId }).clear(),
    EditedAuctionConfigPersistence.clear(),
  ]);
};

/**
 * If there at least 2 numbers in array, we recommend both
 * if not, we only recommend one, if no numbers, we don't recommend
 */
export const getDurationTooltip = (options: number[]) => {
  let suffix = '';
  if (options.length > 1) {
    suffix = ` We recommend ${options[0]} or ${options[1]} days.`;
  } else if (options.length === 1 && typeof options[0] === 'number') {
    suffix = ` We recommend ${getSingularOrPlural({
      word: 'day',
      count: options[0],
    })}.`;
  }
  return `Set the duration for your public sale, or specify an end time.${suffix}`;
};

/**
 * Set a public sale field in editedAuctionConfigState store
 */
export const setEditAuctionConfigPublicSaleField = <
  Key extends keyof RangeEditionSaleScheduleUpdate,
>({
  contractAddress,
  field,
  value,
}: {
  contractAddress: string;
  field: Key;
  value: RangeEditionSaleScheduleUpdate[Key];
}) => {
  const publicSale = getFromList(
    editedAuctionConfigState.fields.editedSaleSchedules[contractAddress],
    schedule => schedule.mintType === 'RangeEdition' && schedule,
  );

  if (!publicSale) return;

  publicSale[field] = value;

  validateEditedAuctionConfigState();
};

/**
 * Set a presale field in editedAuctionConfigState store
 */
export const setEditAuctionConfigPresaleField = <Key extends keyof MerkleDropSaleScheduleUpdate>({
  contractAddress,
  field,
  value,
}: {
  contractAddress: string;
  field: Key;
  value: MerkleDropSaleScheduleUpdate[Key];
}) => {
  const presale = getFromList(
    editedAuctionConfigState.fields.editedSaleSchedules[contractAddress],
    schedule => schedule.mintType === 'MerkleDrop' && schedule,
  );

  if (!presale) return;

  presale[field] = value;

  validateEditedAuctionConfigState();
};

/**
 * Helper to check if given number has decimals
 */
export function containsDecimal<T>(value: T | string | number) {
  return (
    (typeof value === 'number' && value % 1 !== 0) ||
    (typeof value === 'string' && value.includes('.')) ||
    (typeof value === 'string' && value.includes(','))
  );
}

/*
 * Parse value as number, avoiding NaN
 */
export function transformStringInteger(value: string | number | null | undefined) {
  if (!isParseableNumeric(value)) return null;

  const numericValue = parseInt(value.toString());
  return isNumber(numericValue) && !isNaN(numericValue) ? numericValue : null;
}

/*
 * Parse value as float, avoiding NaN
 */
export function transformStringFloat(value: string | number | null | undefined) {
  if (!isParseableNumeric(value)) return null;

  const numericValue = parseFloat(value.toString());
  return isNumber(numericValue) && !isNaN(numericValue) ? numericValue : null;
}
